import { memoize } from 'lodash';
import { createSelector } from 'reselect';
import { State } from '.';
import { CompositeDrug } from '../../models/composite-drug';

const getCompositeDrugFormulationsSelector = createSelector(
    (state: State) => state.byMedId,
    (state: State) => state.byMedNameId,
    (byMedId: Dictionary<CompositeDrug>, byMedNameId: Dictionary<number[]>) => memoize((
        medNameId: Undefinable<number>
    ) => memoize((
        medStrength: Undefinable<string>
    ) => Array.from(new Set((byMedNameId[medNameId || ''] || [])
        .filter(x => byMedId[x]!.medStrength === medStrength)
        .map(x => byMedId[x]!.medDoseForm)))
    ))
);

const getCompositeDrugMedNameIdsByTextSelector = createSelector(
    (state: State) => state.byMedId,
    (state: State) => state.byMedNameId,
    (byMedId: Dictionary<CompositeDrug>, byMedNameId: Dictionary<number[]>) => memoize((
        text: Undefinable<string>
    ) => {
        const textLower = (text || '').toLowerCase();
        return Object.keys(byMedNameId)
            .filter(x => {
                const compositeDrug = byMedId[Array.from(byMedNameId[x]!)[0] || ''];
                return compositeDrug && compositeDrug.medName.toLowerCase().startsWith(textLower);
            })
            .map(x => +x);
    })
);

const getCompositeDrugRouteSelector = createSelector(
    (state: State) => state.byMedId,
    (state: State) => state.byMedNameId,
    (byMedId: Dictionary<CompositeDrug>, byMedNameId: Dictionary<number[]>) => memoize((
        medNameId: Undefinable<number>
    ) => memoize((
        medStrength: Undefinable<string>
    ) => memoize((
        medDoseForm: Undefinable<string>
    ) => Array.from(new Set((byMedNameId[medNameId || ''] || [])
        .filter(x => byMedId[x]!.medStrength === medStrength && byMedId[x]!.medDoseForm === medDoseForm)
        .map(x => byMedId[x]!.medRoute)))
    )))
);

const getCompositeDrugStrengthSelector = createSelector(
    (state: State) => state.byMedId,
    (state: State) => state.byMedNameId,
    (byMedId: Dictionary<CompositeDrug>, byMedNameId: Dictionary<number[]>) => memoize((
        medNameId: Undefinable<number>
    ) => Object.values((byMedNameId[medNameId || ''] || [])
        .filter(x => byMedId[x]!.medStrength)
        .reduce(
            (prevValue, curr) => {
                if (byMedId[curr]!.medStrength) {
                    prevValue[byMedId[curr]!.medStrength!] = [byMedId[curr]!.medStrength || '', byMedId[curr]!.medStrengthUnit || ''];
                }
                return prevValue;
            },
            {}
        )) as string[][]
    )
);

const getOtherCompositeDrugIdsSelector = createSelector(
    (state: State) => state.byMedId,
    (byMedId: Dictionary<CompositeDrug>) => memoize((
        ids: Undefinable<number>[]
    ) => ids.filter(x => x && !byMedId[x]) as number[])
);

export const selectors = {
    checkSearched: (
        state: State,
        text: string
    ): boolean => state.searchedTextHash[text] || false,
    getCompositeDrug: (
        state: State,
        id: Undefinable<number>
    ): Undefinable<CompositeDrug> => state.byMedId[id || ''],
    getCompositeDrugByMedNameId: (
        state: State,
        medNameId: Undefinable<DictionaryKey>
    ): Undefinable<CompositeDrug> => state.byMedId[(state.byMedNameId[medNameId || ''] || [])[0] || ''],
    getCompositeDrugFormulations: (
        state: State,
        medNameId: Undefinable<number>,
        medStrength: Undefinable<string>
    ): string[] => getCompositeDrugFormulationsSelector(state)(medNameId)(medStrength),
    getCompositeDrugIdHash: (
        state: State
    ): Dictionary<number> => state.byCode,
    getCompositeDrugMedNameIdsByText: (
        state: State,
        text: Undefinable<string>
    ): number[] => getCompositeDrugMedNameIdsByTextSelector(state)(text),
    getCompositeDrugRoute: (
        state: State,
        medNameId: Undefinable<number>,
        medStrength: Undefinable<string>,
        medDoseForm: Undefinable<string>
    ): string[] => getCompositeDrugRouteSelector(state)(medNameId)(medStrength)(medDoseForm),
    getCompositeDrugStrength: (
        state: State,
        medNameId: Undefinable<number>
    ): string[][] => getCompositeDrugStrengthSelector(state)(medNameId),
    getLoading: (
        state: State,
        loadingKey: string
    ): boolean => state.loading[loadingKey] || false,
    getOtherCompositeDrugIds: (
        state: State,
        ids: Undefinable<number>[]
    ): number[] => getOtherCompositeDrugIdsSelector(state)(ids)
};
